import { describeElapsed } from "../../lib/labels.js";
import { useElapsed } from "../../lib/useElapsed.js";
import { useSessionStream } from "../../lib/useSessionStream.js";
import { useSiteReady } from "../../lib/useSiteReady.js";
import type { CanvasState } from "../../lib/viewModel.js";
import { useOffersBackend } from "../../lib/xanoHandoff.js";
import { BuildLog } from "./BuildLog.js";
import { DoneView } from "./DoneView.js";
import { ProgressRail } from "./ProgressRail.js";
import { SpecPanel } from "./SpecPanel.js";
import { Header, XanoScreen } from "./XanoScreen.js";
import { stageFor, type Stage } from "./stage.js";

/**
 * Everything from the first activity to the live site.
 *
 * Strictly read-only, for the same reason both other build views are: the API
 * exposes no route that accepts input into a running session, so the guarantee
 * holds by the absence of surface on both sides rather than by a check here.
 *
 * Three states share this component because they are one screen resolving, not
 * three destinations:
 *
 *   building — the rail advancing, the spec panel filling in, the log running.
 *
 *   holding — the build is done and the workspace is published, but the address
 *   is not answering yet. Held here rather than shown as live. The status copy
 *   for `done` already stops one word short of the claim, so this beat costs no
 *   new design; it just lasts as long as it actually needs to.
 *
 *   stopped — the log goes away, because a build with nothing further to report
 *   sitting beside a frozen feed reads as a build still thinking about it.
 */
export function BuildView({ sessionId, onRestart }: { sessionId: string; onRestart: () => void }) {
  const stream = useSessionStream(sessionId);
  const elapsed = useElapsed(stream.createdAt, stream.isTerminal);

  const framing = stream.canvas.state === "framing" ? stream.canvas : null;
  const closed = stream.canvas.state === "closed" ? stream.canvas : null;

  // A build that stopped, whichever way it stopped. Derived from the canvas
  // rather than from `outcome`, because a `succeeded` run that produced no site
  // is also over — reading `outcome` alone leaves the rail advancing and the
  // panel pulsing underneath a message saying nothing came out.
  const stage = stageFor(stream.phase, closed !== null);

  if (framing) {
    // The poll lives in here, and `Reveal` exists only once there is an address
    // to poll for. Calling the hook from this component instead would start its
    // 90s give-up clock at session start: every build longer than that arrives
    // at the reveal with the gate already timed out, and the frame mounts with
    // neither the server probe nor the browser rehearsal ever having run — the
    // exact race those two exist to prevent. React has no conditional hooks, so
    // the condition has to be a component boundary.
    return (
      <Reveal
        sessionId={sessionId}
        siteUrl={framing.siteUrl}
        stream={stream}
        stage={stage}
        elapsed={elapsed}
        onRestart={onRestart}
      />
    );
  }

  return (
    <BuildingScreen
      stream={stream}
      stage={stage}
      elapsed={elapsed}
      closed={closed}
      onRestart={onRestart}
    />
  );
}

/**
 * The hold between "deployed" and "reachable".
 *
 * Exists only while there is an address, which is the whole point: mounting it
 * is what starts the readiness poll, so the poll's budget is spent on the wait
 * it was written for rather than on the build that preceded it.
 */
function Reveal({
  sessionId,
  siteUrl,
  stream,
  stage,
  elapsed,
  onRestart,
}: {
  sessionId: string;
  siteUrl: string;
  stream: ReturnType<typeof useSessionStream>;
  stage: Stage;
  elapsed: number | null;
  onRestart: () => void;
}) {
  // Polls the API, which makes a real request and reads a real status code, and
  // then rehearses the frame's own requests from this browser. See
  // lib/useSiteReady.ts — it carries three separate production bugs' worth of
  // reasoning and is deliberately used unchanged.
  const site = useSiteReady(sessionId, siteUrl);

  // Decided in lib so every skin answers it the same way — see
  // lib/xanoHandoff.ts. The done screen only ever learns whether there is a
  // backend, never how to reach it. Read here rather than at the call site
  // below because the `checking` return underneath is an early exit, and a hook
  // may not sit after one.
  const hasBackend = useOffersBackend({
    outcome: stream.outcome,
    xanoTenant: stream.xanoTenant,
  });

  if (site.status === "checking") {
    // The `done` status copy already says the workspace is published and the
    // address has not answered — it IS the hold, so nothing is overridden while
    // the wait is ordinary. Past the threshold the same unresolved sentence
    // starts reading as a stuck screen rather than a held breath, so it changes
    // to admit the wait while promising the same ending.
    const slow = site.waitedMs > SLOW_HOLD_MS;
    return (
      <BuildingScreen
        stream={stream}
        stage={stage}
        elapsed={elapsed}
        closed={null}
        onRestart={onRestart}
        {...(slow ? SLOW_HOLD : {})}
      />
    );
  }

  const terminalAt = stream.steps.find((s) => s.kind === "terminal")?.at ?? null;

  return (
    <DoneView
      siteUrl={siteUrl}
      prompt={stream.prompt}
      sessionId={sessionId}
      steps={stream.steps}
      fileCount={stream.fileCount}
      // Measured between the two server timestamps rather than taken from the
      // live clock. `useElapsed` starts counting at mount, which is right while
      // watching a build happen and wrong on the path the session URL exists
      // for: reload onto a finished build half an hour later and it would
      // announce that a four-minute build took thirty-four.
      buildMs={
        terminalAt !== null && stream.createdAt !== null
          ? Math.max(0, terminalAt - stream.createdAt)
          : elapsed
      }
      hasBackend={hasBackend}
      // Named on the done screen beside the address, which is the one place a
      // visitor can check they got the framework they picked.
      framework={stream.framework}
      // Passed through untouched, absences and all: the screen decides what to
      // draw for a count it does not have, and lib decides nothing here because
      // there is nothing to decide — these are numbers or they are missing.
      objects={stream.xanoObjects}
      sitePages={stream.sitePages}
      // Only ever set for a build that arrived by SMS, which is the only build
      // whose owner is walking toward the booth holding a phone.
      boothCode={stream.boothCode}
      onRestart={onRestart}
    />
  );
}

/**
 * How long an ordinary hold is allowed to last before the copy admits it.
 *
 * Twenty seconds is roughly where a sentence that has not resolved stops
 * reading as anticipation and starts reading as a screen that has hung.
 */
const SLOW_HOLD_MS = 20_000;

const SLOW_HOLD = {
  statusTitle: "Still coming up",
  statusSub: "The deploy is out and the address is taking its time. Nearly there.",
} as const;

/**
 * The rail, the status, the spec panel, and the log — every state that is not
 * the reveal.
 *
 * One column at every width, centred above the maximum. There is no two-pane
 * variant, so there is one layout to get right and the composition a visitor
 * sees on a phone is the same one a passer-by sees on the booth monitor.
 */
function BuildingScreen({
  stream,
  stage,
  elapsed,
  closed,
  onRestart,
  statusTitle,
  statusSub,
}: {
  stream: ReturnType<typeof useSessionStream>;
  stage: Stage;
  elapsed: number | null;
  closed: Extract<CanvasState, { state: "closed" }> | null;
  onRestart: () => void;
  /** Overrides the phase's own heading and line — see the hold above. */
  statusTitle?: string;
  statusSub?: string;
}) {
  return (
    <XanoScreen fill>
      <Header prompt={stream.prompt}>
        <span className="shrink-0 font-deck-mono text-[11.5px] tabular-nums text-deck-text-faint">
          {!stream.isTerminal && stream.connection === "reconnecting"
            ? // Quiet by design: a brief drop during a long build is normal, and
              // an error banner would alarm someone about a self-healing gap.
              "Reconnecting…"
            : elapsed !== null
              ? describeElapsed(elapsed)
              : ""}
        </span>
      </Header>

      <ProgressRail steps={stage.rail} />

      {/*
        `min-h-0` on this column and on the log inside it is load-bearing. A flex
        child defaults to a content-derived minimum size and refuses to shrink
        below it, which makes the log's own scroll do nothing and turns the frame
        back into an unbounded page.
      */}
      <main className="deck-stack mx-auto flex w-full max-w-[760px] min-h-0 flex-1 flex-col gap-3 px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex shrink-0 items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-[17px] font-semibold sm:text-[19px]">
              {closed ? headingFor(closed.outcome) : (statusTitle ?? stage.statusTitle)}
            </h1>
            <p className="mt-1 text-[13.5px] leading-[1.5] text-pretty text-deck-text-soft">
              {closed
                ? (closed.message ?? "Something went wrong on our side.")
                : (statusSub ?? stage.statusSub)}
            </p>
          </div>
          {/*
            "Start over" in both states, and deliberately not "Cancel" on a live
            build. This control clears the session from the URL and returns the
            visitor to the prompt box; it does not reach the server, and the
            build it left behind runs to completion either way. A button
            labelled "Cancel" would be describing something Hatch has no route
            for.
          */}
          <button
            type="button"
            onClick={onRestart}
            className="-mr-2 h-11 shrink-0 cursor-pointer px-2 text-[13px] font-medium text-deck-text-faint transition-colors hover:text-deck-text-soft"
          >
            Start over
          </button>
        </div>

        <SpecPanel
          phase={stream.phase}
          failed={closed !== null}
          objects={stream.xanoObjects}
          sitePages={stream.sitePages}
          fileCount={stream.fileCount}
        />

        {/* A stopped build has nothing further to report, and a log frozen
            mid-sentence beside a failure message reads as a build still
            thinking about it. */}
        {!closed && (
          <BuildLog
            steps={stream.steps}
            isTerminal={stream.isTerminal}
            fileCount={stream.fileCount}
          />
        )}
      </main>
    </XanoScreen>
  );
}

/** Honest about which ending this was, in this skin's register. */
function headingFor(outcome: string): string {
  switch (outcome) {
    case "succeeded":
      return "Deployed, but nothing came out";
    case "rejected":
      return "No capacity right now";
    case "expired":
      return "This build expired";
    case "cancelled":
      return "Build cancelled";
    default:
      return "The build stopped";
  }
}
